import { useState, useRef, useEffect, useCallback } from "react";
import { Plus, Pencil, Pin, ChevronsRight } from "lucide-react";
import { Icon as IconifyIcon } from "@iconify/react";
import TaitoraUserButton from "@/components/TaitoraUserButton";
import { isLucideKey, getLucideIcon } from "@/config/projectIcons";
import { getProjectInitials, getProjectAccent, activeCircleStyle } from "@/utils/projectInitials";
import { THEME } from "@/config/theme";
import IconPicker from "./IconPicker";
import type { ApiProject } from "@/services/projectService";

interface Props {
  projects: ApiProject[];
  loading: boolean;
  activeId: string | null;
  onSelect: (projectId: string) => void;
  onSetIcon: (projectId: string, iconKey: string) => void;
  onNewWorkspace: () => void;
  isMobile: boolean;
  // Pin state for the hover/pin rail (design-mock `railPinned`). The pin button
  // toggles whether the expanded panel stays open when the pointer leaves.
  pinned: boolean;
  onTogglePin: () => void;
  // True when the rail is itself the canvas-facing edge (tree panel hidden) —
  // rounds its right corners so the pane doesn't read as a hard rectangle.
  roundRight?: boolean;
}

interface PickerState {
  projectId: string;
  top: number;
}

function isIconifyId(icon: string) {
  return icon.includes(":") && !isLucideKey(icon);
}

function CircleContent({ icon, name }: { icon?: string; name: string }) {
  if (icon && isLucideKey(icon)) {
    const LucideIcon = getLucideIcon(icon);
    return LucideIcon ? <LucideIcon size={16} /> : (
      <span className="text-[11px] font-bold tracking-tight">{getProjectInitials(name)}</span>
    );
  }
  if (icon && isIconifyId(icon)) return <IconifyIcon icon={icon} width={18} height={18} />;
  if (icon && /\p{Emoji}/u.test(icon)) return <span className="text-sm leading-none">{icon}</span>;
  return <span className="text-[11px] font-bold tracking-tight">{getProjectInitials(name)}</span>;
}

function ProjectCircle({ project, active, onClick, onOpenPicker }: {
  project: ApiProject;
  active: boolean;
  onClick: () => void;
  onOpenPicker: (top: number) => void;
}) {
  const hasIconify = project.icon && isIconifyId(project.icon);
  const isEmoji = project.icon && !hasIconify && /\p{Emoji}/u.test(project.icon);
  // Mock circle: neutral fill for initials/lucide; light chip only for
  // iconify/emoji glyphs that need contrast. Active picks up the workspace accent.
  const glyphBg = hasIconify || isEmoji;
  const accent = getProjectAccent(project.id);
  const badge = glyphBg
    ? "bg-slate-200 text-slate-900"
    : (active ? "border text-slate-50" : THEME.sidebar.circleIdle);

  return (
    <div className="relative group shrink-0">
      {/* Active accent bar (design mock `barStyle`) — a short accent tab on the
          rail's left edge marking the selected workspace. */}
      {active && (
        <span
          aria-hidden
          style={{ backgroundColor: accent }}
          className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r"
        />
      )}
      <button
        onClick={onClick}
        title={project.name}
        // Active badge picks up the workspace's accent tint/border/glow (mock).
        style={active && !glyphBg ? activeCircleStyle(accent) : undefined}
        className={`
          w-9 h-9 rounded-xl flex items-center justify-center
          transition-all duration-150 select-none
          ${badge}
          ${active ? "" : "opacity-70 hover:opacity-100"}
        `}
      >
        <CircleContent icon={project.icon} name={project.name} />
      </button>

      <button
        data-testid={`edit-icon-${project.id}`}
        title="Change icon"
        onClick={(e) => {
          e.stopPropagation();
          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
          onOpenPicker(rect.top);
        }}
        className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-canvas-accent flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ zIndex: 61 }}
      >
        <Pencil size={8} className="text-white" />
      </button>
    </div>
  );
}

export default function ProjectRail({
  projects,
  loading,
  activeId,
  onSelect,
  onSetIcon,
  onNewWorkspace,
  isMobile,
  pinned,
  onTogglePin,
  roundRight = false,
}: Props) {
  const [picker, setPicker] = useState<PickerState | null>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  const openPicker = useCallback((projectId: string, top: number) => {
    setPicker((prev) => prev?.projectId === projectId ? null : { projectId, top });
  }, []);

  const closePicker = useCallback(() => setPicker(null), []);

  useEffect(() => {
    if (!picker) return;
    const onDown = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        closePicker();
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [picker, closePicker]);

  const activeProject = picker ? projects.find((p) => p.id === picker.projectId) : null;

  return (
    <div
      className={`flex flex-col h-full w-[58px] pt-3 ${THEME.sidebar.railSurface} ${THEME.sidebar.railSeam} shrink-0 ${roundRight ? "rounded-r-2xl" : ""}`}
    >
      {/* Pin toggle — keeps the hover-expanded panel open. Mirrors the mock's
          `pinBtnStyle`: `Pin` when pinned, `ChevronsRight` (peek) when not.
          Desktop only; on mobile the tap-driven overlay + tree lock handle this. */}
      {!isMobile && (
        <div className="flex justify-center pb-2 shrink-0">
          <button
            data-testid="rail-pin"
            onClick={onTogglePin}
            title={pinned ? "Unpin menu" : "Keep menu open"}
            aria-label={pinned ? "Unpin menu" : "Keep menu open"}
            aria-pressed={pinned}
            className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors ${
              pinned ? THEME.sidebar.pinActive : THEME.sidebar.pinIdle
            }`}
          >
            {pinned ? <Pin size={15} /> : <ChevronsRight size={16} />}
          </button>
        </div>
      )}

      {/* Divider */}
      <div className="mx-3 border-b border-white/[0.06] mb-3 shrink-0" />

      {/* Project circles. Scrollbar hidden — its thumb otherwise shows as a stray
          light rectangle in the slim rail (the tree panel has room for one; this
          rail doesn't). */}
      <div className="flex-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex flex-col items-center gap-2.5 px-1.5 py-1">
          {loading && <div className="w-9 h-9 rounded-full bg-white/10 animate-pulse shrink-0" />}
          {projects.map((p) => (
            <ProjectCircle
              key={p.id}
              project={p}
              active={p.id === activeId}
              onClick={() => onSelect(p.id)}
              onOpenPicker={(top) => openPicker(p.id, top)}
            />
          ))}
          <button
            onMouseDown={onNewWorkspace}
            title="New workspace"
            className="w-9 h-9 rounded-xl border border-dashed border-white/15 flex items-center justify-center text-slate-400 hover:text-slate-100 hover:border-white/30 hover:bg-white/5 transition-colors duration-150 shrink-0 mt-1"
          >
            <Plus size={15} />
          </button>
        </div>
      </div>

      {/* User */}
      <div className="flex justify-center pb-3 pt-1 shrink-0">
        <TaitoraUserButton />
      </div>

      {/* Icon picker — fixed so it escapes overflow containers */}
      {picker && (
        <div
          ref={pickerRef}
          data-testid={`icon-picker-${picker.projectId}`}
          style={{ position: "fixed", top: picker.top, left: 60 }}
          className="z-[60] w-64"
        >
          <IconPicker
            currentIcon={activeProject?.icon}
            onSave={(icon) => { onSetIcon(picker.projectId, icon); closePicker(); }}
            onClose={closePicker}
          />
        </div>
      )}
    </div>
  );
}
